import React, { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { dashboardToast } from "../../utils/dashboardToast";
import "bootstrap/dist/css/bootstrap.min.css";

import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import SaListPageShell from "../../components/sa/SaListPageShell";
import ContentLoader from "../../components/ContentLoader";
import RecordDetailModal from "../../components/RecordDetailModal";
import TableSortSelect from "../../components/TableSortSelect";
import { Alert, Button as UiButton, Pagination as UiPagination } from "../../components/ui";
import { TABLE_SORT } from "../../utils/tableSort";
import { getIndustries, addOrUpdateIndustry, deleteIndustry } from "../../services/CostEstimationsetupApi";
import { Factory, Pencil, Search, Trash2 } from "lucide-react";
import "../../assets/css/dashboard.css";

const EmptyCell = () => <span className="app-td__empty">—</span>;

const formatDate = (value?: string) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
};

const IndustryManagement = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [industryToDelete, setIndustryToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [formData, setFormData] = useState({
    id: 0,
    name: "",
    code: "",
    isActive: true
  });

  // Industries data from API
  const [industries, setIndustries] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState(TABLE_SORT.DATE_DESC);
  const [searchTerm, setSearchTerm] = useState("");
  const dataFetchedRef = useRef(false);

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);

  const [apiPageInfo, setApiPageInfo] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalPages: 1,
    totalCount: 0,
  });



  // Fetch industries from API
  const fetchIndustries = useCallback(async (page = apiPageInfo.pageNumber, pageSize = apiPageInfo.pageSize, sort = sortKey, search = searchTerm) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getIndustries({
        searchTerm: search,
        isActive: true,
        page,
        pageSize,
        sort,
      });

      if (response && response.data) {
        setIndustries(response.data.data || []);
        setApiPageInfo(prev => ({
          ...prev,
          pageNumber: page,
          pageSize,
          totalPages: Math.max(1, response.data.totalPages || 1),
          totalCount: response.data.totalRecords || 0
        }));
      }
    } catch (err) {
      console.error('Error fetching industries:', err);
      setError('Failed to fetch industries');
      dashboardToast.error('Failed to fetch industries');
      setIndustries([]);
    } finally {
      setLoading(false);
    }
  }, [apiPageInfo.pageNumber, apiPageInfo.pageSize, sortKey, searchTerm]);

  const handleShow = useCallback((industry = null) => {
    if (industry) {
      setFormData({
        id: industry.id,
        name: industry.name || "",
        code: industry.code || "",
        isActive: industry.isActive !== undefined ? industry.isActive : true
      });
    } else {
      setFormData({
        id: 0,
        name: "",
        code: "",
        isActive: true
      });
    }
    setShowModal(true);
  }, []);

  const handleClose = useCallback(() => setShowModal(false), []);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { name, code, isActive } = formData;
      if (name && code) {
        try {
          setLoading(true);
          const response = await addOrUpdateIndustry({
            id: formData.id,
            name: name.trim(),
            code: code.trim(),
            isActive: isActive
          });

          if (response) {
            dashboardToast.success(formData.id === 0 ? "Industry created successfully" : "Industry updated successfully");
            handleClose();
            // Refresh the industries list
            await fetchIndustries();
          }
        } catch (err) {
          console.error('Error saving industry:', err);
          const errorMessage = err.message || "Failed to save industry";
          dashboardToast.error(errorMessage);
        } finally {
          setLoading(false);
        }
      } else {
        dashboardToast.error("Please fill in all required fields");
      }
    },
    [formData, handleClose, fetchIndustries]
  );

  const handleDelete = useCallback((industry) => {
    setIndustryToDelete(industry);
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!industryToDelete) return;

    try {
      setLoading(true);
      const response = await deleteIndustry(industryToDelete.id);

      dashboardToast.success("Industry deleted successfully");
      setShowDeleteModal(false);
      setIndustryToDelete(null);

      // Refresh the industries list
      await fetchIndustries();
    } catch (err) {
      console.error('Error deleting industry:', err);
      const errorMessage = err.message || "Failed to delete industry";
      dashboardToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [industryToDelete, fetchIndustries]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setIndustryToDelete(null);
  }, []);

  const openViewModal = useCallback((industry) => {
    setViewRecord(industry);
    setShowViewModal(true);
  }, []);

  const closeViewModal = useCallback(() => {
    setShowViewModal(false);
    setViewRecord(null);
  }, []);

  const goToPage = useCallback(n => {
    const pg = Math.min(Math.max(n, 1), apiPageInfo.totalPages);
    if (pg !== apiPageInfo.pageNumber) {
      fetchIndustries(pg, apiPageInfo.pageSize);
    }
  }, [apiPageInfo.pageNumber, apiPageInfo.totalPages, apiPageInfo.pageSize, fetchIndustries]);

  useEffect(() => {
    fetchIndustries(apiPageInfo.pageNumber, apiPageInfo.pageSize);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!dataFetchedRef.current) {
      dataFetchedRef.current = true;
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      fetchIndustries(1, apiPageInfo.pageSize, sortKey, searchTerm);
    }, 300);

    return () => window.clearTimeout(timerId);
    // eslint-disable-next-line
  }, [searchTerm]);

  const handleSortChange = (nextSort) => {
    setSortKey(nextSort);
    fetchIndustries(1, apiPageInfo.pageSize, nextSort, searchTerm);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };





  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={
        <Menu
          onSelect={handleMenuSelection}
          selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />
      }
    >
      <SaListPageShell topBarTitle="Industry Management">
        <div className="dash-frame-parent1">
          <div className="dash-list-toolbar">
            <div className="dash-list-toolbar__intro">
              <h5 className="dash-list-toolbar__title">Manage Industries</h5>
              <p className="dash-list-toolbar__desc">
                Create and manage industries used across assessments and setup.
              </p>
            </div>
            <div className="dash-list-toolbar__actions">
              <div className="dash-search-field dash-table-search">
                <Search className="dash-search-field__icon" aria-hidden />
                <input
                  type="text"
                  placeholder="Search by name…"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="dash-search-field__input"
                  disabled={loading}
                />
              </div>
              <TableSortSelect variant="chip"
                value={sortKey}
                onChange={handleSortChange}
                disabled={loading}
              />
              <UiButton variant="primary" className="sa-create-btn" onClick={() => handleShow()}>
                Create Industry
              </UiButton>
            </div>
          </div>

          {loading ? (
            <ContentLoader label="Loading industries…" />
          ) : error ? (
            <Alert variant="error">{error}</Alert>
          ) : (
            <Fragment>
              <div className="app-table-wrap app-table-wrap--refined">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Code</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {industries.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center">
                          <span className="app-td__desc">No industries found.</span>
                        </td>
                      </tr>
                    ) : (
                      industries.map((industry, index) => {
                        const displayName = industry.name || "Untitled industry";
                        const rowNumber =
                          (apiPageInfo.pageNumber - 1) * apiPageInfo.pageSize + index + 1;

                        return (
                          <tr
                            key={industry.id}
                            className="rdm-row-clickable"
                            onClick={() => openViewModal(industry)}
                          >
                            <td>
                              <span className="app-td__index">{rowNumber}</span>
                            </td>
                            <td>
                              <span className="app-td__title">{displayName}</span>
                            </td>
                            <td>
                              {industry.code ? (
                                <span className="app-td__meta">{industry.code}</span>
                              ) : (
                                <EmptyCell />
                              )}
                            </td>
                            <td>
                              <span
                                className={
                                  industry.isActive
                                    ? "app-status app-status--success"
                                    : "app-status app-status--muted"
                                }
                              >
                                {industry.isActive ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td>
                              <span className="app-td__meta">{formatDate(industry.createdAt)}</span>
                            </td>
                            <td
                              className="dash-mobile-table-actions"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="btn-list-actions">
                                <UiButton
                                  variant="outline"
                                  size="sm"
                                  className="btn-list-edit"
                                  title="Edit"
                                  aria-label={`Edit ${displayName}`}
                                  onClick={() => handleShow(industry)}
                                >
                                  <Pencil />
                                </UiButton>
                                <UiButton
                                  variant="danger"
                                  size="sm"
                                  className="btn-list-delete"
                                  title="Delete"
                                  aria-label={`Delete ${displayName}`}
                                  onClick={() => handleDelete(industry)}
                                  disabled={loading}
                                >
                                  <Trash2 />
                                </UiButton>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <UiPagination
                page={apiPageInfo.pageNumber}
                totalPages={apiPageInfo.totalPages}
                onPageChange={goToPage}
              />
            </Fragment>
          )}
        </div>
      </SaListPageShell>

      {/* Create/Edit Modal */}
      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        size="lg"
        dialogClassName="rounded-3"
      >
        <Modal.Header closeButton className="dashboard-modal-header">
          <Factory className="h-5 w-5 me-2 " />
          <Modal.Title>{formData.id ? "Edit Industry" : "Create Industry"}</Modal.Title>
        </Modal.Header>

        <Form onSubmit={handleSubmit}>
          <Modal.Body className="px-4">
            <Row>
              {/* Name */}
              <Col md={6}>
                <Form.Group controlId="formName" className="mb-3">
                  <Form.Label className="required">Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter industry name"
                  />
                </Form.Group>
              </Col>
              {/* Code */}
              <Col md={6}>
                <Form.Group controlId="formCode" className="mb-3">
                  <Form.Label className="required">Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    required
                    placeholder="Enter industry code"
                    maxLength="10"
                    style={{ textTransform: 'uppercase' }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              {/* Active Status */}
              <Col md={12}>
                <Form.Group controlId="formIsActive" className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    label="Active"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="btn btn-dashboard-primary"
            >
              {loading ? "Saving..." : "Save Industry"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={showDeleteModal} onHide={cancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete{" "}
          <strong>{industryToDelete?.name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelDelete} disabled={loading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      <RecordDetailModal
        show={showViewModal}
        onHide={closeViewModal}
        title={viewRecord?.name || "Industry Details"}
        subtitle="Industry record"
        fields={[
          { label: "Name", value: viewRecord?.name },
          { label: "Code", value: viewRecord?.code },
          { label: "Active", value: Boolean(viewRecord?.isActive) },
          {
            label: "Created At",
            value: viewRecord?.createdAt
              ? new Date(viewRecord.createdAt).toLocaleDateString()
              : undefined,
          },
        ]}
        actions={[
          {
            type: "edit",
            onClick: () => {
              const industry = viewRecord;
              closeViewModal();
              if (industry) handleShow(industry);
            },
          },
          {
            type: "delete",
            onClick: () => {
              const industry = viewRecord;
              closeViewModal();
              if (industry) handleDelete(industry);
            },
          },
        ]}
      />

    </DashboardShell>
  );
};

export default IndustryManagement;
